/* Form to add a YouTube playlist subscription. Auto-fetches playlist metadata from URL. */

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation, useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ListVideo } from "lucide-react";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";

interface AddPlaylistFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function AddPlaylistForm({ onSuccess, footerContainer }: AddPlaylistFormProps) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [videoCount, setVideoCount] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [startImmediately, setStartImmediately] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPlaylist = useMutation(api.playlists.create);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);
  const startPlaylist = useMutation(api.playlists.start);
  const getPlaylistInfo = useAction(api.playlists.getPlaylistInfo);
  const enqueueFirstVideo = useAction(api.playlists.enqueueFirstVideo);

  const fetchAbortRef = useRef(0);

  // Auto-fetch playlist info when URL changes
  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed || !looksLikePlaylistUrl(trimmed)) {
      setThumbnailUrl(null);
      setVideoCount(null);
      return;
    }

    const fetchId = ++fetchAbortRef.current;
    setIsFetching(true);

    const timer = setTimeout(async () => {
      try {
        const info = await getPlaylistInfo({ url: trimmed });
        if (fetchId !== fetchAbortRef.current) return;
        if (info) {
          if (!nameManuallyEdited) setName(info.playlistName);
          setChannelName(info.channelName);
          setChannelId(info.channelId);
          setThumbnailUrl(info.thumbnailUrl);
          setVideoCount(info.videoCount);
        }
      } catch {
        // Non-fatal — user can still type name manually
      } finally {
        if (fetchId === fetchAbortRef.current) setIsFetching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [url, getPlaylistInfo, nameManuallyEdited]);

  const canSubmit =
    url.trim().length > 0 &&
    name.trim().length > 0 &&
    !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const playlistId = await createPlaylist({
        name: name.trim(),
        playlistUrl: url.trim(),
        channelId: channelId ?? undefined,
        channelName: channelName ?? undefined,
        tags: tags.length > 0 ? tags : undefined,
        thumbnailUrl: thumbnailUrl ?? undefined,
        totalVideos: videoCount ?? undefined,
      });
      if (startImmediately) {
        await startPlaylist({ id: playlistId });
        // Enqueue the first video immediately (fire-and-forget)
        enqueueFirstVideo({ playlistId }).catch((err) => {
          console.warn("[AddPlaylistForm] Failed to enqueue first video:", err);
        });
      }
      posthog.capture(Events.PLAYLIST_ADDED, { name: name.trim(), startImmediately, videoCount: videoCount ?? undefined });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add playlist";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-2">
        <Label htmlFor="playlist-url">YouTube Playlist URL</Label>
        <Input
          id="playlist-url"
          placeholder="https://youtube.com/playlist?list=..."
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          autoComplete="off"
        />
      </div>

      {/* Playlist preview */}
      {(thumbnailUrl || name) && !isFetching && (
        <div className="flex items-center gap-3 rounded-lg border p-3">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={name}
              className="shrink-0 w-20 h-[45px] rounded-sm object-cover bg-muted"
            />
          ) : (
            <div className="shrink-0 w-20 h-[45px] rounded-sm bg-muted flex items-center justify-center">
              <ListVideo className="size-4 text-muted-foreground/50" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight truncate">{name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {channelName && <span>{channelName}</span>}
              {channelName && videoCount !== null && <span>·</span>}
              {videoCount !== null && (
                <span>{videoCount} {videoCount === 1 ? "video" : "videos"}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {isFetching && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="size-4 animate-spin" />
          Fetching playlist info...
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="playlist-name">Playlist Name</Label>
        <Input
          id="playlist-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameManuallyEdited(true);
          }}
          placeholder={url.trim() ? "Fetching name..." : "Enter URL above to auto-detect"}
          disabled={!url.trim()}
        />
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <SourceTagSelect
          selectedTags={tags}
          onTagsChange={setTags}
          availableTags={availableTags}
          onCreateTag={async (tag) => { await addTag({ tag }); }}
        />
      </div>

      {/* Start immediately checkbox */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={startImmediately}
          onChange={(e) => setStartImmediately(e.target.checked)}
          className="rounded border-border"
        />
        <span className="text-sm">Start adding videos immediately</span>
      </label>

      <p className="text-xs text-muted-foreground">
        The first video will be added to your feed right away. After that, one new video is added each day once you complete the current one.
      </p>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add Playlist
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add Playlist
        </Button>
      )}
    </div>
  );
}

function looksLikePlaylistUrl(url: string): boolean {
  return url.includes("list=") || url.includes("youtube.com/playlist");
}
